import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Menu
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupIcon from '@mui/icons-material/Group';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FlagIcon from '@mui/icons-material/Flag';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { API_URL } from '../config';
import { authedFetch } from '../utils/apiUtils';
import { useSeasonYearContext } from '../context/SeasonYearContext';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNotification } from '../context/NotificationContext';
import { DashboardStatsSkeleton, TableSkeleton } from './common/SkeletonLoaders';
import { EmptyState } from './common/EmptyState';
import { currencyFormatter } from '../utils/formatters';
import type { LostRevenue } from '../types/lostRevenue';

dayjs.extend(utc);

// Type definitions
interface DriverStats {
  totalDrivers: number;
  totalCompetingDrivers: number;
  activeDrivers: number;
  nonMembersWithResults: number;
  driversWith5PlusRaces: number;
  totalFeatureWins: number;
  mostFeatureWins: number;
}

interface TrackStats {
  totalTracks: number;
  activeTracks: number;
  statesRepresented: number;
}

interface RaceStats {
  totalRaces: number;
  upcomingRaces: number;
  completedRaces: number;
  activeDivisions: number;
  activeRegions: number;
  activeSeries: number;
  averageCarCount: number;
  maxCarCount: number;
}

interface DivisionStat {
  division_name: string;
  race_count: number;
  unique_drivers: number;
  avg_car_count: number;
  series_count?: number;
}

interface RegionStat {
  region_name: string;
  race_count: number;
  unique_drivers: number;
  series_count?: number;
}

interface SeriesStat {
  series_name: string;
  series_code: string;
  division_name: string;
  race_count: number;
  unique_drivers: number;
  avg_car_count: number;
}

interface StandingsDriver {
  position: number;
  driver_id: number;
  first_name: string;
  last_name: string;
  car_number: string | null;
  points: number;
  gap_to_leader: number;
}

interface DivisionStandings {
  division_id: number;
  division_code: string;
  division_name: string;
  last_updated_at: string | null;
  field_depth: {
    contenders: number;
    total: number;
  };
  top: StandingsDriver[];
}

interface StandingsSnapshot {
  year: number;
  divisions: DivisionStandings[];
}

interface PendingEntryEvent {
  event_id: number;
  event_date: string;
  event_name: string | null;
  track_name: string;
  division_name: string;
  division_code: string;
}

interface PendingEntry {
  year: number;
  days: number;
  count: number;
  events: PendingEntryEvent[];
}

interface DashboardStats {
  drivers: DriverStats;
  tracks: TrackStats;
  races: RaceStats;
  divisionStats: DivisionStat[];
  regionStats: RegionStat[];
  seriesStats: SeriesStat[];
}

interface RecentRace {
  event_id: number;
  id?: number;
  event_name?: string;
  event_date: string;
  date?: string;
  track_name: string;
  track_city?: string;
  track_state?: string;
  division_name?: string;
  division?: string;
  region_name?: string;
  region?: string;
  series_name?: string;
  race_type?: string;
  driver_count?: number;
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  valueColor?: string;
  secondaryValueColor?: string;
  secondaryValue?: number | string;
  secondaryLabel?: string;
  subtitle?: string;
  info?: string;
}

const InfoTooltip: React.FC<{ title: string; sx?: object }> = ({ title, sx }) => (
  <Tooltip title={title} arrow placement="top" enterDelay={200}>
    <InfoOutlinedIcon
      sx={{
        fontSize: 14,
        color: 'text.disabled',
        ml: 0.5,
        cursor: 'help',
        verticalAlign: 'middle',
        '&:hover': { color: 'text.secondary' },
        ...sx
      }}
    />
  </Tooltip>
);

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color = 'primary',
  valueColor,
  secondaryValueColor,
  secondaryValue,
  secondaryLabel,
  subtitle,
  info
}) => {
  const resolvedValueColor = valueColor ?? (color === 'primary' ? 'text.primary' : `${color}.dark`);
  const resolvedSecondaryValueColor = secondaryValueColor ?? 'text.disabled';
  const iconColor = color === 'primary' ? 'text.disabled' : `${color}.main`;
  const isStatus = color === 'warning' || color === 'error';
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {isStatus && (
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: `${color}.main` }} />
            )}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
            >
              {title}
              {info && <InfoTooltip title={info} />}
            </Typography>
          </Box>
          {icon && (
            <Box sx={{ color: iconColor, display: 'flex', alignItems: 'center', '& svg': { fontSize: 18 } }}>
              {icon}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="h4" color={resolvedValueColor} sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
            {value}
          </Typography>
          {secondaryValue !== undefined && (
            <>
              <Typography variant="h6" color="text.disabled" sx={{ fontWeight: 400 }}>
                /
              </Typography>
              <Typography variant="h6" color={resolvedSecondaryValueColor} sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                {secondaryValue}
              </Typography>
            </>
          )}
        </Box>
        {(subtitle || secondaryLabel) && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {subtitle || secondaryLabel}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const formatRelativeTime = (iso: string | null): string => {
  if (!iso) return 'no entries yet';
  const then = dayjs.utc(iso);
  const now = dayjs.utc();
  const minutes = now.diff(then, 'minute');
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = now.diff(then, 'hour');
  if (hours < 24) return `${hours}h ago`;
  const days = now.diff(then, 'day');
  return `${days}d ago`;
};

const MEDAL_STYLES: Record<number, { bg: string; fg: string }> = {
  1: { bg: '#facc15', fg: '#713f12' },
  2: { bg: '#cbd5e1', fg: '#334155' },
  3: { bg: '#d97706', fg: '#ffffff' }
};

const PositionMedal: React.FC<{ position: number }> = ({ position }) => {
  const style = MEDAL_STYLES[position];
  if (!style) {
    return (
      <Typography variant="body2" color="text.disabled" fontWeight={600} sx={{ textAlign: 'center' }}>
        {position}
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        bgcolor: style.bg,
        color: style.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      {position}
    </Box>
  );
};

const depthBadgeStyle = (contenders: number, total: number) => {
  if (total === 0) {
    return { bg: 'transparent', fg: 'text.secondary', border: 'transparent' };
  }
  const ratio = contenders / total;
  if (ratio >= 0.30) return { bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0' };
  if (ratio >= 0.10) return { bg: '#fffbeb', fg: '#b45309', border: '#fde68a' };
  return { bg: '#f4f4f5', fg: '#71717a', border: '#e4e4e7' };
};

const DivisionStandingsCard: React.FC<{ division: DivisionStandings; onClick?: () => void }> = ({ division, onClick }) => {
  const { contenders, total } = division.field_depth;
  const depth = depthBadgeStyle(contenders, total);
  return (
    <Card
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 120ms, box-shadow 120ms',
        '&:hover': onClick ? { backgroundColor: 'action.hover' } : undefined,
        '&:focus-visible': onClick
          ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
          : undefined
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {division.division_name}
          </Typography>
          <Tooltip
            title="Contenders are drivers with 5+ races in this division this season. Green = deep field (30%+), amber = thin field (10-29%), gray = shallow."
            arrow
            placement="top"
            enterDelay={200}
          >
            <Box
              sx={{
                cursor: 'help',
                px: 1,
                py: 0.25,
                borderRadius: 9999,
                bgcolor: depth.bg,
                color: depth.fg,
                border: '1px solid',
                borderColor: depth.border,
                fontSize: 11,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.4
              }}
            >
              {contenders} / {total}
            </Box>
          </Tooltip>
        </Box>
        {division.top.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No standings yet
          </Typography>
        ) : (
          division.top.map((driver) => (
            <Box
              key={driver.driver_id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '24px 1fr auto',
                gap: 1,
                alignItems: 'center',
                py: 0.5,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              <PositionMedal position={driver.position} />
              <Typography variant="body2" noWrap>
                {driver.first_name} {driver.last_name}
                {driver.car_number ? ` #${driver.car_number}` : ''}
              </Typography>
              <Typography variant="body2">
                {driver.points}
                {driver.position > 1 && driver.gap_to_leader > 0 && (
                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                    -{driver.gap_to_leader}
                  </Typography>
                )}
              </Typography>
            </Box>
          ))
        )}
        <Tooltip
          title="Time since the most recent race result was entered for this division. Standings shift as more weekend results get added."
          arrow
          placement="top"
          enterDelay={200}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block', borderTop: 1, borderColor: 'divider', pt: 0.75, cursor: 'help' }}
          >
            Updated {formatRelativeTime(division.last_updated_at)}
          </Typography>
        </Tooltip>
      </CardContent>
    </Card>
  );
};

const initialStats: DashboardStats = {
  drivers: {
    totalDrivers: 0,
    totalCompetingDrivers: 0,
    activeDrivers: 0,
    nonMembersWithResults: 0,
    driversWith5PlusRaces: 0,
    totalFeatureWins: 0,
    mostFeatureWins: 0
  },
  tracks: {
    totalTracks: 0,
    activeTracks: 0,
    statesRepresented: 0
  },
  races: {
    totalRaces: 0,
    upcomingRaces: 0,
    completedRaces: 0,
    activeDivisions: 0,
    activeRegions: 0,
    activeSeries: 0,
    averageCarCount: 0,
    maxCarCount: 0
  },
  divisionStats: [],
  regionStats: [],
  seriesStats: []
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { seasonYear, defaultSeasonYear, setSeasonYear } = useSeasonYearContext();
  const { user } = useAuth();
  const [yearOptions, setYearOptions] = useState<number[]>([defaultSeasonYear]);
  const [seasonAnchorEl, setSeasonAnchorEl] = useState<null | HTMLElement>(null);
  const seasonMenuOpen = Boolean(seasonAnchorEl);

  useEffect(() => {
    authedFetch(`${API_URL}/config/available-seasons`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data: { years: number[] }) => {
        if (data.years?.length > 0) setYearOptions(data.years);
      })
      .catch(() => {});
  }, []);
  const [recentRaces, setRecentRaces] = useState<RecentRace[]>([]);
  const [stats, setStats] = useState<DashboardStats>(initialStats);
  const [lostRevenue, setLostRevenue] = useState<LostRevenue | null>(null);
  const [standingsSnapshot, setStandingsSnapshot] = useState<StandingsSnapshot | null>(null);
  const [pendingEntry, setPendingEntry] = useState<PendingEntry | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const racesResponse = await authedFetch(`${API_URL}/races/recent?year=${seasonYear}`);
      if (!racesResponse.ok) {
        const errorText = racesResponse.status === 404 
          ? 'Race data not found. Please check your connection.'
          : racesResponse.status === 500
          ? 'Server error occurred. Please try again later.'
          : 'Unable to load recent races. Please refresh the page.';
        throw new Error(errorText);
      }
      const racesData = await racesResponse.json();
      setRecentRaces(Array.isArray(racesData) ? racesData : []);

      // Fetch stats
      const statsResponse = await authedFetch(`${API_URL}/dashboard/stats?year=${seasonYear}`);
      if (!statsResponse.ok) {
        const errorText = statsResponse.status === 404
          ? 'Statistics data not found. Please check your connection.'
          : statsResponse.status === 500
          ? 'Server error loading statistics. Please try again later.'
          : 'Unable to load dashboard statistics. Please refresh the page.';
        throw new Error(errorText);
      }
      const statsData = await statsResponse.json();
      
      // Ensure all required properties exist with default values
      setStats({
        drivers: {
          totalDrivers: statsData?.drivers?.total_drivers || 0,
          totalCompetingDrivers: statsData?.drivers?.total_competing_drivers || 0,
          activeDrivers: statsData?.drivers?.active_drivers || 0,
          nonMembersWithResults: statsData?.drivers?.non_members_with_results || 0,
          driversWith5PlusRaces: statsData?.drivers?.drivers_5_plus_races || 0,
          totalFeatureWins: statsData?.drivers?.total_feature_wins || 0,
          mostFeatureWins: statsData?.drivers?.most_feature_wins || 0
        },
        tracks: {
          totalTracks: statsData?.tracks?.total_tracks || 0,
          activeTracks: statsData?.tracks?.active_tracks || 0,
          statesRepresented: statsData?.tracks?.states_represented || 0
        },
        races: {
          totalRaces: statsData?.races?.total_races || 0,
          upcomingRaces: statsData?.races?.upcoming_races || 0,
          completedRaces: statsData?.races?.completed_races || 0,
          activeDivisions: statsData?.races?.active_divisions || 0,
          activeRegions: statsData?.races?.active_regions || 0,
          activeSeries: statsData?.races?.active_series || 0,
          averageCarCount: statsData?.races?.avg_car_count || 0,
          maxCarCount: statsData?.races?.max_car_count || 0
        },
        divisionStats: statsData?.divisionStats || [],
        regionStats: statsData?.regionStats || [],
        seriesStats: statsData?.seriesStats || []
      });

      const [lostRevenueRes, standingsRes, pendingRes] = await Promise.all([
        authedFetch(`${API_URL}/dashboard/lost-revenue?year=${seasonYear}`),
        authedFetch(`${API_URL}/dashboard/standings-snapshot?year=${seasonYear}`),
        authedFetch(`${API_URL}/dashboard/pending-entry?year=${seasonYear}`)
      ]);

      // Degrade a single bad/secondary response to an empty card instead of
      // failing the whole refresh (which also gates lastFetchedAt + success).
      const safeJson = async (res: Response) => {
        if (!res.ok) return null;
        try {
          return await res.json();
        } catch {
          return null;
        }
      };
      setLostRevenue(await safeJson(lostRevenueRes));
      setStandingsSnapshot(await safeJson(standingsRes));
      setPendingEntry(await safeJson(pendingRes));

      setLastFetchedAt(new Date().toISOString());

      // Show success message only if this was a manual refresh (not initial load)
      if (!loading) {
        showSuccess('Dashboard data refreshed successfully');
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data. Please try again.';
      setError(errorMessage);
      if (!loading) {
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loading, showSuccess, showError, seasonYear]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonYear]);

  if (loading) {
    return (
      <Box sx={{ m: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>Dashboard</Typography>
        <DashboardStatsSkeleton />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <TableSkeleton rows={5} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <TableSkeleton rows={5} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ m: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Welcome back, {user?.username || 'User'}
            </Typography>
            <Typography variant="h4">
              Dashboard
            </Typography>
          </Box>
          <Button
            onClick={(e) => setSeasonAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              ml: 1,
              borderRadius: 9999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' }
            }}
          >
            <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75, fontWeight: 500 }}>
              Season
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {seasonYear}
            </Typography>
          </Button>
          <Menu
            anchorEl={seasonAnchorEl}
            open={seasonMenuOpen}
            onClose={() => setSeasonAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 100 } } }}
          >
            {yearOptions.map((y) => (
              <MenuItem
                key={y}
                selected={y === seasonYear}
                onClick={() => {
                  setSeasonYear(y);
                  setSeasonAnchorEl(null);
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastFetchedAt && (
            <Typography variant="caption" color="text.secondary">
              Updated {formatRelativeTime(lastFetchedAt)}
            </Typography>
          )}
          <Tooltip title="Refresh data">
            <IconButton
              onClick={fetchDashboardData}
              disabled={refreshing}
              sx={{
                '& .MuiSvgIcon-root': {
                  transition: 'transform 0.6s ease-in-out',
                  transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Drivers"
            value={stats.drivers.activeDrivers}
            valueColor="success.dark"
            secondaryValue={stats.drivers.nonMembersWithResults}
            secondaryValueColor="error.dark"
            secondaryLabel="Members / Non-Members"
            icon={<GroupIcon />}
            info="Members have a paid DIRTcar membership for the current season. Non-members raced but never registered."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Races"
            value={stats.races.completedRaces}
            secondaryValue={stats.races.upcomingRaces}
            secondaryLabel="Completed / Upcoming"
            icon={<FlagIcon />}
            info="Events scheduled this season, split by completed vs still upcoming."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Avg Car Count"
            value={Math.round(stats.races.averageCarCount)}
            subtitle={`Season high ${stats.races.maxCarCount}`}
            icon={<DirectionsCarIcon />}
            info="Average cars per event across the season. Bigger field = healthier division."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Pending Entry"
            value={pendingEntry?.count ?? 0}
            subtitle="Events missing feature results"
            color="warning"
            icon={<PendingActionsIcon />}
            info="Events from the last 21 days whose feature race has no results entered yet. Heats and qualifiers don't count."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Lost Revenue"
            value={currencyFormatter.format(lostRevenue?.total ?? 0)}
            subtitle="Non-members in priced divisions"
            color="error"
            icon={<MoneyOffIcon />}
            info="What we'd have collected if non-members in priced divisions had registered. Each driver counted once, at their highest division price."
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2, mt: 1 }}>
            <Typography variant="h5">
              Standings
              <InfoTooltip title="Top 3 drivers per active division by season points. National and series-level only; regional standings live on the standings page." />
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              Top 3 per division. National / series-level only.
            </Typography>
          </Box>
          {standingsSnapshot && standingsSnapshot.divisions.length > 0 ? (
            <Grid container spacing={2}>
              {standingsSnapshot.divisions.map((division) => (
                <Grid item key={division.division_id} xs={12} sm={6} md={4}>
                  <DivisionStandingsCard division={division} onClick={() => navigate('/standings')} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Typography color="text.secondary" variant="body2">
                No standings to display for this season yet.
              </Typography>
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Pending Entry
              <InfoTooltip title="Past events from the last 21 days whose feature race has no results entered yet. Click a row to jump to its race entry page." />
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Events from the last 21 days missing feature results
            </Typography>
            {pendingEntry && pendingEntry.events.length > 0 ? (
              <List dense disablePadding>
                {pendingEntry.events.map((event, idx) => (
                  <React.Fragment key={event.event_id}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      disableGutters
                      sx={{
                        cursor: 'pointer',
                        py: 0.75,
                        px: 1,
                        mx: -1,
                        borderRadius: 1,
                        transition: 'background-color 120ms',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          '& .pending-entry-cta': { color: 'primary.main', opacity: 1 }
                        }
                      }}
                      onClick={() => navigate(`/race-entry?event=${event.event_id}`)}
                    >
                      <ListItemText
                        primary={`${event.track_name} — ${event.division_name}`}
                        secondary={dayjs.utc(event.event_date).format('MMM D')}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Box
                        className="pending-entry-cta"
                        sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', opacity: 0.6, transition: 'opacity 120ms, color 120ms' }}
                      >
                        <Typography variant="caption" sx={{ mr: 0.25 }}>
                          Enter
                        </Typography>
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No events pending entry.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Recent Races */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Recent Races
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/races')}
              >
                View All
              </Button>
            </Box>
            {Array.isArray(recentRaces) && recentRaces.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Track</TableCell>
                      <TableCell>Division</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell align="right">Cars</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentRaces.map((race) => (
                      <TableRow
                        key={`race-${race.event_id || race.id}-${race.race_type || 'main'}`}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/race-entry?event=${race.event_id}`)}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {dayjs.utc(race.event_date || race.date).format('MMM D')}
                        </TableCell>
                        <TableCell>{race.track_name}</TableCell>
                        <TableCell>{race.division_name || race.division}</TableCell>
                        <TableCell>{race.region_name || race.region}</TableCell>
                        <TableCell align="right">{race.driver_count || 0}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/race-entry?event=${race.event_id}`); }}>
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyState
                type="races"
                message="No recent races to display. Start by creating a new race event."
                actionLabel="Create Race"
                onAction={() => navigate('/race-entry')}
              />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <MoneyOffIcon color="error" />
                <Typography variant="h6">
                  Lost Revenue from Non-Members
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" color="error.main">
                  {currencyFormatter.format(lostRevenue?.total ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {lostRevenue?.totalNonMembers ?? 0} non-members, each counted once at their highest division price
                </Typography>
              </Box>
            </Box>
            {lostRevenue && lostRevenue.divisions.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Division</TableCell>
                      <TableCell align="right">Non-Members</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Lost Revenue</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lostRevenue.divisions.map((division) => {
                      const goToDetail = () => navigate(`/dashboard/lost-revenue/${division.division_id}?year=${seasonYear}`);
                      return (
                        <TableRow
                          key={`lost-rev-${division.division_code}`}
                          hover
                          sx={{ cursor: 'pointer' }}
                          role="button"
                          tabIndex={0}
                          aria-label={`View lost revenue detail for ${division.division_name}`}
                          onClick={goToDetail}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              goToDetail();
                            }
                          }}
                        >
                          <TableCell>{division.division_name}</TableCell>
                          <TableCell align="right">{division.non_member_count}</TableCell>
                          <TableCell align="right">{currencyFormatter.format(division.price)}</TableCell>
                          <TableCell align="right">{currencyFormatter.format(division.lost_revenue)}</TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={(e) => { e.stopPropagation(); goToDetail(); }}>
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" variant="body2">
                No non-members with race results in priced divisions for this season.
              </Typography>
            )}
          </Paper>
        </Grid>

      </Grid>

    </Box>
  );
};

export default Dashboard;
